import type {FC} from "react";
import {Badge, Box, Button, Flex, Text} from "@chakra-ui/react";
import {useCreditsStore} from "../../store/useCreditsStore.ts";
import {
  badgeStyles,
  currentSubscription,
  dateLineFor,
  formatCredits,
  planFraction,
  planHeadingFor,
  planStatusFor,
  statusBadgeStyle,
  tabularNums,
  textButtonStyle,
} from "./subscriptionPresentation.ts";

interface PlanSummaryLineProps {
  onManageBilling: () => void;
}

// The Plans view's one-line stand-in for the plan card; the card, its actions and the history live on Billing.
export const PlanSummaryLine: FC<PlanSummaryLineProps> = ({onManageBilling}) => {
  const balance = useCreditsStore((state) => state.balance);
  if (balance?.schemaVersion !== 2) return null;

  const subscription = currentSubscription(balance);
  const status = planStatusFor(balance, subscription);
  const facts = [
    ...(subscription ? [dateLineFor(subscription), `${planFraction(balance)} plan credits left`] : []),
    ...(balance.unlimited ? [] : [`${formatCredits(balance.nonPlanCredits)} extra credits`]),
  ];
  return (
    <Flex
      as="section"
      aria-label="Current plan"
      align="center"
      gap={4}
      py={2}
      pl={5}
      pr={2}
      rounded="2xl"
      bg="n.200"
      data-testid="plan-summary"
    >
      <Flex align="center" gap={2} flexShrink={0}>
        <Text fontSize="md" fontWeight="semibold" color="n.700" data-testid="plan-summary-name">
          {planHeadingFor(balance, subscription)}
        </Text>
        {status && (
          <Badge {...statusBadgeStyle} {...badgeStyles[status.tone]} data-testid="plan-summary-status">
            {status.label}
          </Badge>
        )}
      </Flex>
      {facts.length > 0 && (
        <Flex
          as="ul"
          flex="1"
          minW={0}
          wrap="wrap"
          align="center"
          columnGap={2}
          rowGap={1}
          fontSize="sm"
          color="n.500"
          sx={tabularNums}
        >
          {facts.map((fact, index) => (
            <Flex as="li" key={fact} align="center" gap={2}>
              {index > 0 && <Box as="span" aria-hidden="true" w={1} h={1} rounded="full" bg="n.400"/>}
              {fact}
            </Flex>
          ))}
        </Flex>
      )}
      <Button {...textButtonStyle} ml="auto" flexShrink={0} onClick={onManageBilling}>Manage billing</Button>
    </Flex>
  );
};
